import React from 'react';
import { cn } from '@utils/cn';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  trend?: string;
  trendUp?: boolean;
  action?: string;
  onClick?: () => void;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  variant = 'primary',
  trend,
  trendUp,
  action,
  onClick,
  className,
}) => {
  const variants = {
    primary: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'bg-blue-100 text-blue-600',
      text: 'text-blue-600',
    },
    secondary: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      icon: 'bg-emerald-100 text-emerald-600',
      text: 'text-emerald-600',
    },
    success: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'bg-green-100 text-green-600',
      text: 'text-green-600',
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'bg-amber-100 text-amber-600',
      text: 'text-amber-600',
    },
    error: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'bg-red-100 text-red-600',
      text: 'text-red-600',
    },
    info: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'bg-purple-100 text-purple-600',
      text: 'text-purple-600',
    },
  };

  const variantClasses = variants[variant];

  return (
    <div
      className={cn(
        "border-2 rounded-lg p-6 transition-all duration-200",
        variantClasses.bg,
        variantClasses.border,
        onClick && "cursor-pointer hover:shadow-md active:scale-[0.98]",
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", variantClasses.icon)}>
          {icon}
        </div>
      </div>

      <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
      <p className="text-3xl font-bold text-gray-900 mb-3">{value}</p>

      {(trend || action) && (
        <div className="flex items-center justify-between">
          {trend && (
            <span className={cn(
              "text-sm font-medium flex items-center gap-1",
              trendUp !== undefined 
                ? trendUp 
                  ? "text-green-600" 
                  : "text-red-600"
                : "text-gray-600"
            )}>
              {trendUp !== undefined && (
                <span>{trendUp ? '↑' : '↓'}</span>
              )}
              {trend}
            </span>
          )}
          {action && (
            <span className={cn("text-sm font-medium", variantClasses.text)}>
              {action} →
            </span>
          )}
        </div>
      )}
    </div>
  );
};
